import { useState, useRef } from 'react';
import { wowRetention } from '../../../lib/adoptionAnalytics.js';
import { fmtWk, sum, PALETTE, AREA_COLOR, USE_COLOR, NEW_COLOR } from './util.js';

export function LoyaltyChart({ agg }) {
  const N = agg.weeks.length;
  if (!N) return null;
  const W = Math.max(820, 60 + N * 30), H = 320, m = { t: 14, r: 48, b: 40, l: 38 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / N, bw = Math.min(24, band * 0.66);
  const xc = (i) => m.l + band * i + band / 2;
  const maxY = Math.max(5, Math.ceil(Math.max(1, ...agg.activeUsers) / 5) * 5);
  const rates = wowRetention(agg);
  const yr = (p) => m.t + ph - ph * p;
  const pts = [];
  for (let i = 0; i < N; i++) if (rates[i] != null) pts.push([xc(i), yr(rates[i]), Math.round(rates[i] * 100), i]);
  const grid = [0, 0.2, 0.4, 0.6, 0.8, 1].map((f) => ({ y: m.t + ph - ph * f, l: Math.round(maxY * f), r: Math.round(f * 100) }));
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      {grid.map((g, i) => (
        <g key={i}>
          <line x1={m.l} y1={g.y} x2={m.l + pw} y2={g.y} className="grid" />
          <text x={m.l - 6} y={g.y + 3} className="axis" textAnchor="end">{g.l}</text>
          <text x={m.l + pw + 6} y={g.y + 3} textAnchor="start" fill={USE_COLOR} fontSize="10">{g.r}</text>
        </g>
      ))}
      {agg.weeks.map((w, i) => {
        const ret = agg.returningUsers[i], nw = agg.newUsers[i];
        const hRet = (ph * ret) / maxY, hNew = (ph * nw) / maxY, x = xc(i) - bw / 2, yRet = m.t + ph - hRet;
        return (
          <g key={w}>
            {ret > 0 && <rect x={x} y={yRet} width={bw} height={hRet} fill={AREA_COLOR} rx="1"><title>{`${fmtWk(w)} — Returning: ${ret}`}</title></rect>}
            {nw > 0 && <rect x={x} y={yRet - hNew} width={bw} height={hNew} fill={NEW_COLOR} rx="1"><title>{`${fmtWk(w)} — New: ${nw}`}</title></rect>}
            {i % (N > 18 ? 2 : 1) === 0 && <text x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(w)}</text>}
          </g>
        );
      })}
      {pts.slice(1).map((p, k) => <line key={k} x1={pts[k][0]} y1={pts[k][1]} x2={p[0]} y2={p[1]} stroke={USE_COLOR} strokeWidth="2" />)}
      {pts.map((p, k) => <circle key={k} cx={p[0]} cy={p[1]} r="3" fill={USE_COLOR}><title>{`${fmtWk(agg.weeks[p[3]])} — WoW retention: ${p[2]}%`}</title></circle>)}
    </svg>
  );
}

/**
 * Usage chart. mode='company' stacks by company; mode='user' stacks by end user
 * (top 10 + Other); mode='total' draws the total-trend area.
 */
export function UsageChart({ agg, mode = 'company' }) {
  const N = agg.weeks.length;
  if (!N) return null;
  const W = Math.max(820, 60 + N * 30), H = 320, m = { t: 14, r: 16, b: 40, l: 40 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / N, bw = Math.min(24, band * 0.66);
  const xc = (i) => m.l + band * i + band / 2;
  const maxY = Math.max(5, Math.ceil(Math.max(1, ...agg.totalUserDays) / 5) * 5);
  const yb = (v) => m.t + ph - (ph * v) / maxY;

  let series = [];
  if (mode === 'company') {
    const ranked = [...agg.companies].sort((a, b) => sum(agg.compUserDays[b]) - sum(agg.compUserDays[a]));
    const top = ranked.slice(0, 8), rest = ranked.slice(8);
    series = top.map((c, i) => ({ label: c, color: PALETTE[i % PALETTE.length], data: agg.compUserDays[c] }));
    if (rest.length) series.push({ label: `Other (${rest.length})`, color: '#9aa3b2', data: agg.weeks.map((_, i) => rest.reduce((s, c) => s + agg.compUserDays[c][i], 0)) });
  } else if (mode === 'user') {
    const ranked = [...agg.users].sort((a, b) => sum(agg.userMatrix[b]) - sum(agg.userMatrix[a]));
    const top = ranked.slice(0, 10), rest = ranked.slice(10);
    series = top.map((u, i) => ({ label: u.split('@')[0], full: u, color: PALETTE[i % PALETTE.length], data: agg.userMatrix[u] }));
    if (rest.length) series.push({ label: `Other (${rest.length})`, color: '#9aa3b2', data: agg.weeks.map((_, i) => rest.reduce((s, u) => s + agg.userMatrix[u][i], 0)) });
  }
  const grid = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ y: m.t + ph - ph * f, v: Math.round(maxY * f) }));

  return (
    <>
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
        {grid.map((g, i) => (
          <g key={i}>
            <line x1={m.l} y1={g.y} x2={m.l + pw} y2={g.y} className="grid" />
            <text x={m.l - 6} y={g.y + 3} className="axis" textAnchor="end">{g.v}</text>
          </g>
        ))}
        {mode === 'total'
          ? (() => {
            const path = agg.totalUserDays.map((v, i) => `${i ? 'L' : 'M'} ${xc(i)} ${yb(v)}`).join(' ');
            return (
              <g>
                <path d={`${path} L ${xc(N - 1)} ${m.t + ph} L ${xc(0)} ${m.t + ph} Z`} fill="rgba(91,141,239,.16)" />
                <path d={path} fill="none" stroke={AREA_COLOR} strokeWidth="2" />
                {agg.weeks.map((w, i) => (
                  <g key={w}>
                    <circle cx={xc(i)} cy={yb(agg.totalUserDays[i])} r="3" fill={AREA_COLOR}><title>{`${fmtWk(w)} — ${agg.totalUserDays[i]} user-days`}</title></circle>
                    {i % (N > 18 ? 2 : 1) === 0 && <text x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(w)}</text>}
                  </g>
                ))}
              </g>
            );
          })()
          : agg.weeks.map((w, i) => {
            let acc = 0; const x = xc(i) - bw / 2;
            return (
              <g key={w}>
                {series.map((s) => {
                  const v = s.data[i]; if (v <= 0) return null;
                  const h = (ph * v) / maxY, y = m.t + ph - h - (ph * acc) / maxY; acc += v;
                  return <rect key={s.label} x={x} y={y} width={bw} height={h} fill={s.color} rx="1"><title>{`${fmtWk(w)} — ${s.full || s.label}: ${v} user-day(s)`}</title></rect>;
                })}
                {i % (N > 18 ? 2 : 1) === 0 && <text x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(w)}</text>}
              </g>
            );
          })}
      </svg>
      {mode !== 'total' && (
        <div className="legend">
          {series.map((s) => <span key={s.label} className="leg"><span className="sw" style={{ background: s.color }} />{s.label}</span>)}
        </div>
      )}
    </>
  );
}

export function HBars({ counts, order, color, denom }) {
  const max = Math.max(1, ...order.map((k) => counts[k] || 0));
  const rows = order.filter((k) => (counts[k] || 0) > 0);
  if (!rows.length) return <div className="muted">No classified prompts.</div>;
  return (
    <div className="hbars">
      {rows.map((k) => {
        const n = counts[k] || 0, pct = denom ? Math.round((100 * n) / denom) : null;
        return (
          <div className="hbar" key={k}>
            <div className="hbar-lbl" title={k}>{k}</div>
            <div className="hbar-track"><div className="hbar-fill" style={{ width: `${(100 * n) / max}%`, background: color }} /></div>
            <div className="hbar-n">{n}{pct != null ? <span className="dim"> ({pct}%)</span> : null}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Combo chart for the adoption report: weekly interactions (bars, left axis) +
 * cumulative scientists and cumulative champions (lines, right axis).
 * Drag horizontally to zoom into a week range; double-click (or Reset) to zoom out.
 * Hover shows a crosshair tooltip with exact values.
 */
export function AdoptionChart({ report, label = 'users' }) {
  const { weeks, weekInteractions, cumScientists, cumChampions } = report;
  const N = weeks.length;
  const [range, setRange] = useState(null);       // [lo, hi] absolute indices, or null = full
  const [hover, setHover] = useState(null);       // slice index
  const [drag, setDrag] = useState(null);         // { a, b } slice indices while dragging
  const wrapRef = useRef(null);
  if (!N) return null;

  const lo = range ? range[0] : 0;
  const hi = range ? range[1] : N - 1;
  const idx = []; for (let i = lo; i <= hi; i++) idx.push(i);
  const n = idx.length;

  const W = 760, H = 340, m = { t: 16, r: 52, b: 42, l: 46 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / n, bw = Math.min(34, band * 0.5);
  const cx = (s) => m.l + band * s + band / 2;       // s = slice index
  const sliceBars = idx.map((i) => weekInteractions[i]);
  const sliceSci = idx.map((i) => cumScientists[i]);
  const sliceCh = idx.map((i) => cumChampions[i]);
  const maxBar = Math.max(5, Math.ceil(Math.max(1, ...sliceBars) / 10) * 10);
  const maxLine = Math.max(5, Math.ceil(Math.max(1, ...sliceSci) / 5) * 5);
  const yb = (v) => m.t + ph - (ph * v) / maxBar;
  const yl = (v) => m.t + ph - (ph * v) / maxLine;
  const grid = [0, 0.25, 0.5, 0.75, 1];
  const linePath = (vals) => vals.map((v, s) => `${s ? 'L' : 'M'} ${cx(s)} ${yl(v)}`).join(' ');
  const labelEvery = Math.max(1, Math.ceil(n / 12));
  const showPointLabels = n <= 14;

  const sliceFromEvent = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    return Math.max(0, Math.min(n - 1, Math.round((svgX - m.l - band / 2) / band)));
  };
  const onMove = (e) => { const s = sliceFromEvent(e); setHover(s); if (drag) setDrag({ a: drag.a, b: s }); };
  const onDown = (e) => { const s = sliceFromEvent(e); setDrag({ a: s, b: s }); };
  const onUp = () => {
    if (drag && drag.a !== drag.b) {
      const a = Math.min(drag.a, drag.b) + lo, b = Math.max(drag.a, drag.b) + lo;
      setRange([a, b]);
    }
    setDrag(null);
  };
  const reset = () => { setRange(null); setHover(null); setDrag(null); };

  const hv = hover != null ? hover : null;
  const tip = hv != null ? {
    week: weeks[idx[hv]],
    interactions: sliceBars[hv],
    sci: sliceSci[hv],
    ch: sliceCh[hv],
    leftPct: (cx(hv) / W) * 100,
  } : null;

  return (
    <div className="adopt-chart" ref={wrapRef} style={{ position: 'relative' }}>
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}
        onMouseMove={onMove} onMouseLeave={() => { setHover(null); setDrag(null); }}
        onMouseDown={onDown} onMouseUp={onUp} onDoubleClick={reset}
        style={{ cursor: drag ? 'ew-resize' : 'crosshair' }}>
        {/* gridlines + dual axis ticks */}
        {grid.map((f, i) => (
          <g key={i}>
            <line x1={m.l} y1={m.t + ph - ph * f} x2={m.l + pw} y2={m.t + ph - ph * f} className="grid" />
            <text x={m.l - 7} y={m.t + ph - ph * f + 3} className="axis" textAnchor="end">{Math.round(maxBar * f)}</text>
            <text x={m.l + pw + 7} y={m.t + ph - ph * f + 3} fill="var(--teal)" fontSize="10" textAnchor="start">{Math.round(maxLine * f)}</text>
          </g>
        ))}
        {/* axis titles */}
        <text transform={`rotate(-90 12 ${m.t + ph / 2})`} x="12" y={m.t + ph / 2} className="axis" textAnchor="middle">Interactions / week</text>
        <text transform={`rotate(90 ${W - 10} ${m.t + ph / 2})`} x={W - 10} y={m.t + ph / 2} fill="var(--teal)" fontSize="10" textAnchor="middle">Cumulative {label}</text>

        {/* drag selection band */}
        {drag && drag.a !== drag.b && (
          <rect x={Math.min(cx(drag.a), cx(drag.b))} y={m.t} width={Math.abs(cx(drag.b) - cx(drag.a))} height={ph} fill="rgba(91,141,239,.15)" stroke="rgba(91,141,239,.5)" />
        )}
        {/* hover crosshair */}
        {tip && <line x1={cx(hv)} y1={m.t} x2={cx(hv)} y2={m.t + ph} stroke="var(--mut)" strokeDasharray="3 3" opacity="0.6" />}

        {/* bars */}
        {idx.map((i, s) => {
          const h = (ph * sliceBars[s]) / maxBar;
          return (
            <g key={weeks[i]}>
              {sliceBars[s] > 0 && <rect x={cx(s) - bw / 2} y={m.t + ph - h} width={bw} height={h} fill="#bcd6d4" rx="1" />}
              {s % labelEvery === 0 && <text x={cx(s)} y={H - m.b + 15} className="axis" textAnchor="middle">{fmtWk(weeks[i])}</text>}
            </g>
          );
        })}

        {/* lines */}
        <path d={linePath(sliceCh)} fill="none" stroke="#d98b3a" strokeWidth="2" strokeDasharray="5 4" />
        <path d={linePath(sliceSci)} fill="none" stroke="var(--teal)" strokeWidth="2.5" />
        {idx.map((i, s) => (
          <g key={`p${weeks[i]}`}>
            <circle cx={cx(s)} cy={yl(sliceSci[s])} r="3.5" fill="var(--teal)" />
            <circle cx={cx(s)} cy={yl(sliceCh[s])} r="2.5" fill="#d98b3a" />
            {showPointLabels && <text x={cx(s)} y={yl(sliceSci[s]) - 7} fill="var(--teal)" fontSize="10" fontWeight="600" textAnchor="middle">{sliceSci[s]}</text>}
          </g>
        ))}
      </svg>

      {tip && (
        <div className="adopt-tip" style={{ left: `${tip.leftPct}%` }}>
          <div className="adopt-tip-wk">{fmtWk(tip.week)}</div>
          <div><span className="sw-bar" /> {tip.interactions} interactions</div>
          <div><span className="sw-sci" /> {tip.sci} cumulative {label}</div>
          <div><span className="sw-ch" /> {tip.ch} champions</div>
        </div>
      )}

      <div className="adopt-chart-hint">
        <button className="zoom-reset" onClick={reset} disabled={!range}>Reset zoom</button>
        <span className="dim">
          {range ? `Showing ${fmtWk(weeks[lo])}–${fmtWk(weeks[hi])} · ` : ''}
          Drag across the chart to zoom · double-click to reset
        </span>
      </div>
    </div>
  );
}

/** Generic single-series line over weeks (values aligned to weeks). */
export function MiniLine({ weeks, values, color = AREA_COLOR, max, unit = '', height = 200 }) {
  const N = weeks.length;
  if (!N) return null;
  const W = Math.max(560, 50 + N * 26), H = height, m = { t: 14, r: 14, b: 36, l: 38 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b;
  const xc = (i) => m.l + (N === 1 ? pw / 2 : (pw * i) / (N - 1));
  const mx = max ?? Math.max(1, ...values);
  const yb = (v) => m.t + ph - (ph * v) / mx;
  const path = values.map((v, i) => `${i ? 'L' : 'M'} ${xc(i)} ${yb(v)}`).join(' ');
  const grid = [0, 0.5, 1];
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      {grid.map((f, i) => (
        <g key={i}>
          <line x1={m.l} y1={m.t + ph - ph * f} x2={m.l + pw} y2={m.t + ph - ph * f} className="grid" />
          <text x={m.l - 6} y={m.t + ph - ph * f + 3} className="axis" textAnchor="end">{Math.round(mx * f)}{unit}</text>
        </g>
      ))}
      <path d={`${path} L ${xc(N - 1)} ${m.t + ph} L ${xc(0)} ${m.t + ph} Z`} fill="rgba(91,141,239,.14)" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {values.map((v, i) => (
        <g key={i}>
          <circle cx={xc(i)} cy={yb(v)} r="2.5" fill={color}><title>{`${fmtWk(weeks[i])} — ${v}${unit}`}</title></circle>
          {i % (N > 16 ? 2 : 1) === 0 && <text x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(weeks[i])}</text>}
        </g>
      ))}
    </svg>
  );
}

/** Growth-accounting diverging bars: new/retained/resurrected up, churned down. */
export function GrowthBars({ g }) {
  const N = g.weeks.length;
  if (!N) return null;
  const W = Math.max(560, 50 + N * 30), H = 260, m = { t: 14, r: 14, b: 32, l: 36 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / N, bw = Math.min(26, band * 0.6);
  const xc = (i) => m.l + band * i + band / 2;
  const up = g.weeks.map((_, i) => g.newU[i] + g.retained[i] + g.resurrected[i]);
  const maxUp = Math.max(1, ...up), maxDown = Math.max(1, ...g.churned);
  const scale = Math.max(maxUp, maxDown);
  const zero = m.t + (ph * scale) / (2 * scale); // mid
  const yScale = (ph / 2) / scale;
  const segs = [['retained', '#5b8def'], ['newU', '#37c98b'], ['resurrected', '#f5a623']];
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      <line x1={m.l} y1={zero} x2={m.l + pw} y2={zero} stroke="var(--mut)" opacity="0.5" />
      {g.weeks.map((w, i) => {
        let acc = 0;
        return (
          <g key={w}>
            {segs.map(([key, color]) => {
              const v = g[key][i]; if (v <= 0) return null;
              const h = v * yScale, y = zero - (acc + v) * yScale; acc += v;
              return <rect key={key} x={xc(i) - bw / 2} y={y} width={bw} height={h} fill={color}><title>{`${fmtWk(w)} — ${key}: ${v}`}</title></rect>;
            })}
            {g.churned[i] > 0 && <rect x={xc(i) - bw / 2} y={zero} width={bw} height={g.churned[i] * yScale} fill="#e9576b"><title>{`${fmtWk(w)} — churned: ${g.churned[i]}`}</title></rect>}
            {i % (N > 16 ? 2 : 1) === 0 && <text x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(w)}</text>}
          </g>
        );
      })}
    </svg>
  );
}

const cohortColor = (pct) => {
  if (pct <= 0) return '#161a22';
  const t = pct / 100;
  const r = Math.round(29 + (55 - 29) * t), gg = Math.round(33 + (201 - 33) * t), b = Math.round(48 + (138 - 48) * t);
  return `rgb(${r},${gg},${b})`;
};

/** Cohort-retention triangle as a colored table (weeks 0..maxK after join). */
export function CohortHeatmap({ cohort, maxK = 10 }) {
  if (!cohort.cohorts.length) return <div className="muted">No cohorts.</div>;
  const K = Math.min(maxK, Math.max(...cohort.cohorts.map((c) => c.ret.length)));
  return (
    <div className="heat">
      <table className="hm">
        <thead>
          <tr><th className="usr">Cohort (first week)</th><th>Users</th>{Array.from({ length: K }, (_, k) => <th key={k}>W{k}</th>)}</tr>
        </thead>
        <tbody>
          {cohort.cohorts.map((c) => (
            <tr key={c.week}>
              <td className="usr">{fmtWk(c.week)}</td>
              <td style={{ color: 'var(--mut)' }}>{c.size}</td>
              {Array.from({ length: K }, (_, k) => {
                const cell = c.ret[k];
                return <td key={k} style={{ background: cell ? cohortColor(cell.pct) : 'transparent', color: '#dce7f5' }} title={cell ? `${cell.pct}% (${cell.count}/${c.size})` : ''}>{cell ? cell.pct : ''}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Stacked-area chart over weeks. series = [{label,data[]}], colors optional. */
export function StackedArea({ weeks, series, colors = PALETTE, height = 280, unit = '' }) {
  const N = weeks.length;
  if (!N || !series.length) return null;
  const W = Math.max(620, 50 + N * 26), H = height, m = { t: 14, r: 14, b: 34, l: 40 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b;
  const xc = (i) => m.l + (N === 1 ? pw / 2 : (pw * i) / (N - 1));
  const totals = weeks.map((_, i) => series.reduce((s, sr) => s + (sr.data[i] || 0), 0));
  const maxY = Math.max(1, ...totals);
  const yb = (v) => m.t + ph - (ph * v) / maxY;
  const cum = weeks.map(() => 0);
  const areas = series.map((sr, si) => {
    const lower = weeks.map((_, i) => cum[i]);
    weeks.forEach((_, i) => { cum[i] += sr.data[i] || 0; });
    const upper = weeks.map((_, i) => cum[i]);
    const top = upper.map((v, i) => `${i ? 'L' : 'M'} ${xc(i)} ${yb(v)}`).join(' ');
    const bot = lower.map((v, i) => `L ${xc(N - 1 - i)} ${yb(lower[N - 1 - i])}`).join(' ');
    return { d: `${top} ${bot} Z`, color: colors[si % colors.length] };
  });
  const grid = [0, 0.5, 1];
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
      {grid.map((f, i) => (
        <g key={i}>
          <line x1={m.l} y1={m.t + ph - ph * f} x2={m.l + pw} y2={m.t + ph - ph * f} className="grid" />
          <text x={m.l - 6} y={m.t + ph - ph * f + 3} className="axis" textAnchor="end">{Math.round(maxY * f)}{unit}</text>
        </g>
      ))}
      {areas.map((a, i) => <path key={i} d={a.d} fill={a.color} opacity="0.85"><title>{series[i].label}</title></path>)}
      {weeks.map((w, i) => (i % (N > 16 ? 2 : 1) === 0
        ? <text key={w} x={xc(i)} y={H - m.b + 14} className="axis" textAnchor="middle">{fmtWk(w)}</text> : null))}
    </svg>
  );
}

/** Lorenz curve of usage inequality with the line of equality. */
export function LorenzCurve({ lorenz, gini }) {
  if (!lorenz || lorenz.length < 2) return null;
  const S = 240, m = 30, pw = S - m - 10, ph = S - m - 10;
  const X = (x) => m + (pw * x) / 100;
  const Y = (y) => (S - 10) - (ph * y) / 100;
  const path = lorenz.map((p, i) => `${i ? 'L' : 'M'} ${X(p.x)} ${Y(p.y)}`).join(' ');
  return (
    <svg className="chart" viewBox={`0 0 ${S} ${S}`} width={S} height={S} style={{ maxWidth: S }}>
      <line x1={X(0)} y1={Y(0)} x2={X(100)} y2={Y(100)} stroke="var(--mut)" strokeDasharray="4 3" opacity="0.6" />
      <path d={`${path} L ${X(100)} ${Y(0)} Z`} fill="rgba(91,141,239,.18)" />
      <path d={path} fill="none" stroke={AREA_COLOR} strokeWidth="2" />
      <text x={m} y={S - 12} className="axis">% users</text>
      <text x={S / 2} y={14} className="axis" textAnchor="middle">Gini {gini}</text>
    </svg>
  );
}

/** Day-of-week (Mon..Sun) × hour activity heatmap. */
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function CalendarHeatmap({ cal }) {
  if (!cal || !cal.max) return <div className="muted">No activity.</div>;
  const col = (v) => {
    if (v <= 0) return '#161a22';
    const t = Math.min(1, v / cal.max);
    const r = Math.round(29 + (91 - 29) * t), g = Math.round(33 + (141 - 33) * t), b = Math.round(48 + (239 - 48) * t);
    return `rgb(${r},${g},${b})`;
  };
  return (
    <div className="heat">
      <table className="hm cal">
        <thead><tr><th className="usr">UTC</th>{Array.from({ length: 24 }, (_, h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>
          {cal.matrix.map((row, d) => (
            <tr key={d}>
              <td className="usr" style={{ minWidth: 44 }}>{DOW[d]}</td>
              {row.map((v, h) => <td key={h} style={{ background: col(v), color: 'transparent' }} title={`${DOW[d]} ${h}:00 UTC — ${v}`}>.</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Decreasing funnel bars. stages = [{stage,count,pct}]. */
export function FunnelBars({ stages }) {
  return (
    <div className="funnel">
      {stages.map((s, i) => (
        <div className="funnel-row" key={s.stage}>
          <div className="funnel-lbl">{s.stage}</div>
          <div className="funnel-track"><div className="funnel-fill" style={{ width: `${s.pct}%`, background: PALETTE[i % PALETTE.length] }} /></div>
          <div className="funnel-val">{s.count} <span className="dim">({s.pct}%)</span></div>
        </div>
      ))}
    </div>
  );
}

const heatColor = (v) => (v <= 0 ? '#1d2130' : v === 1 ? '#2f5a8a' : v === 2 ? '#4d86c4' : v === 3 ? '#73b0e6' : '#a9d2f5');

export function Heatmap({ agg, users }) {
  const list = users || agg.users;
  if (!list.length) return null;
  return (
    <div className="heat">
      <table className="hm">
        <thead>
          <tr><th className="usr">User</th>{agg.weeks.map((w) => <th key={w}>{fmtWk(w)}</th>)}</tr>
        </thead>
        <tbody>
          {list.map((u) => (
            <tr key={u}>
              <td className="usr" title={u}>{u}</td>
              {agg.userMatrix[u].map((v, i) => (
                <td key={i} style={{ background: heatColor(v) }} title={`${u} — ${v} active day(s)`}>{v > 0 ? v : ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
